import { useI18n } from 'vue-i18n';
import { toCents } from '@Helpers';
import { FORM_VALIDATIONS } from '../constants';
import type { FieldSchema } from '../types';
import { emailValidation, microchipValidation, phoneValidation } from '../helpers';

export default function () {
  const { t } = useI18n();
  // CASES
  function emailFieldValidation(field: FieldSchema) {
    const ERROR_MESSAGE = 'components.forms.errors.validEmail';
    const { state, model } = field;

    if (!model) return state.errors.delete(ERROR_MESSAGE);

    const isValid = emailValidation(model);
    if (isValid) return state.errors.delete(ERROR_MESSAGE);

    state.errors.add(ERROR_MESSAGE);
  }

  function microchipFieldValidation(field: FieldSchema) {
    const ERROR_MESSAGE = 'components.forms.errors.requiredMicrochipLength';
    const { state, model } = field;

    if (!model) return state.errors.delete(ERROR_MESSAGE);

    const isValid = microchipValidation(model);
    if (isValid) return state.errors.delete(ERROR_MESSAGE);

    state.errors.add(ERROR_MESSAGE);
  }

  function amountFieldValidation(field: FieldSchema) {
    const ERROR_MESSAGE = 'components.forms.errors.amount';
    const ERROR_MESSAGE_MISSING_UNIT = 'components.forms.errors.amountMissingUnit';

    const { state, model } = field;

    if (!model) return state.errors.delete(ERROR_MESSAGE);

    const { amount, amount_unit } = model;

    state.errors.delete(ERROR_MESSAGE);
    state.errors.delete(ERROR_MESSAGE_MISSING_UNIT);

    if (amount && typeof amount !== 'string') {
      model.amount = String(amount);
    }

    if ((amount && !amount_unit) || (amount_unit && !amount)) {
      state.errors.add(ERROR_MESSAGE_MISSING_UNIT);
      model.value = undefined;
      return;
    }

    if (!amount) {
      model.value = undefined;
      state.errors.delete(ERROR_MESSAGE);
      state.errors.delete(ERROR_MESSAGE_MISSING_UNIT);
      return;
    }

    if (amount_unit) {
      state.errors.delete(ERROR_MESSAGE_MISSING_UNIT);
    }

    if (typeof amount !== 'string' || amount !== amount.trim()) {
      state.errors.add(ERROR_MESSAGE);
      model.value = undefined;
      return;
    }

    // NOTE: This portion of the code was done by Gerald.
    const doseRegex = /^((\d{0,4})?([.,]\d{1,4})?|\d{1,4} \d{1,4}\/\d{1,4}|\d{1,4}\/\d{1,4})$/;
    if (!doseRegex.test(amount)) {
      state.errors.add(ERROR_MESSAGE);
      model.value = undefined;
      return;
    }
    try {
      if (amount.includes('/')) {
        const parts = amount.split(' ');
        let whole = 0,
          numerator = 0,
          denominator = 1;
        if (parts.length === 2) {
          whole = parseInt(parts[0], 10);
          [numerator, denominator] = parts[1].split('/').map(Number);
        } else {
          [numerator, denominator] = parts[0].split('/').map(Number);
        }
        if (
          denominator === 0 ||
          whole.toString().length > 4 ||
          numerator.toString().length > 4 ||
          denominator.toString().length > 4
        ) {
          state.errors.add(ERROR_MESSAGE);
          model.value = undefined;
          return;
        }
        state.errors.delete(ERROR_MESSAGE);
        return;
      }
      if (amount.includes('.') || amount.includes(',')) {
        const normalized = amount.replace(',', '.');
        const [intPart, decimalPart] = normalized.split('.');
        if (intPart.length > 4 || decimalPart.length > 4) {
          state.errors.add(ERROR_MESSAGE);
          model.value = undefined;
          return;
        }
        state.errors.delete(ERROR_MESSAGE);
        return;
      }
      if (amount.length > 4) {
        state.errors.add(ERROR_MESSAGE);
        model.value = undefined;
        return;
      }
      state.errors.delete(ERROR_MESSAGE);
    } catch (e) {
      state.errors.add(ERROR_MESSAGE);
      model.value = undefined;
    }

    state.errors.delete(ERROR_MESSAGE);
    state.errors.delete(ERROR_MESSAGE_MISSING_UNIT);
  }

  function phoneFieldValidation(field: FieldSchema) {
    const ERROR_MESSAGE = 'components.forms.errors.validPhone';
    const { state, model } = field;

    if (!model) return state.errors.delete(ERROR_MESSAGE);

    const isValid = phoneValidation(model);
    if (isValid) return state.errors.delete(ERROR_MESSAGE);

    state.errors.add(ERROR_MESSAGE);
  }

  function emailTypeFieldValidation(field: FieldSchema) {
    const EMPTY_EMAIL = 'components.forms.errors.onePrimaryEmailRequired';
    const { state, model } = field;

    if (!model) return state.errors.delete(EMPTY_EMAIL);

    const hasPrimary = field.model.filter((entry: any) => entry.primary).length;
    if (hasPrimary) return state.errors.delete(EMPTY_EMAIL);

    state.errors.add(EMPTY_EMAIL);
  }

  function contactTypeFieldValidation(field: FieldSchema) {
    const ERROR_MESSAGE =
      field.name === 'emails' ? 'components.forms.errors.validEmailReview' : 'components.forms.errors.validPhoneReview';

    const { state, model } = field;

    const hasError = model?.some((entry: any) => entry.error);

    if (hasError) return state.errors.add(ERROR_MESSAGE);

    state.errors.delete(ERROR_MESSAGE);
  }

  function stripeFieldValidation(field: FieldSchema) {
    const ERROR_MESSAGE = 'components.forms.errors.requiredStripe';
    const STRIPE_ERROR_KEY = 'components.forms.errors.stripeValidationError';
    const { state, model, settings } = field;

    if (!settings.required) {
      state.errors.delete(ERROR_MESSAGE);
      state.errors.delete(STRIPE_ERROR_KEY);
      return;
    }

    // Check if there's a Stripe validation error (e.g., missing zip code, invalid card)
    // This error is synced from the Stripe component's cardError ref
    const hasStripeError = model?.stripeError || state.errors.has(STRIPE_ERROR_KEY);
    if (hasStripeError) {
      state.errors.add(STRIPE_ERROR_KEY);
      state.errors.delete(ERROR_MESSAGE);
      return;
    }

    // Check if required fields are empty (no token means card is invalid or not validated)
    const emptyModelOnRequiredField = !model || !model.cardHolderName || !model.creditCardToken;
    if (emptyModelOnRequiredField) {
      state.errors.add(ERROR_MESSAGE);
      state.errors.delete(STRIPE_ERROR_KEY);
      return;
    }

    // All validations passed
    state.errors.delete(ERROR_MESSAGE);
    state.errors.delete(STRIPE_ERROR_KEY);
  }

  function splitPaymentFieldValidation(field: FieldSchema, wasFormSubmitted: boolean) {
    const { state, model, settings } = field;

    if (!settings.required || !wasFormSubmitted) return state.errors.clear();

    // Minimum payment methods validation
    const amountFields = ['cash_amount', 'check_amount', 'other_amount', 'card_reader_amount', 'credit_card_amount'];
    const paymentMethods = amountFields.map((fieldName) => ({
      name: fieldName,
      amount: model[fieldName] || 0,
    }));

    const nonZeroPaymentMethods = paymentMethods.filter(
      ({ amount }) => amount !== null && amount !== undefined && Number(amount) > 0
    );

    if (nonZeroPaymentMethods.length < 2) {
      state.errors.clear();
      return state.errors.add('cart.splitPayment.minimumPaymentMethodsValidation');
    }

    const splitTotalCents = toCents(model.split_total);
    const cartTotalCents = toCents(model.cart_total);

    if (splitTotalCents !== cartTotalCents) {
      state.errors.clear();
      return state.errors.add('cart.splitPayment.totalAmountValidation');
    }

    const missinCreditCardData = Number(model.credit_card_amount) > 0 && (!model.name_on_card || !model.token);
    if (missinCreditCardData) {
      state.errors.clear();
      return state.errors.add('components.forms.errors.requiredCreditCard');
    }

    state.errors.clear();
  }

  function moneyFieldValidation(field: FieldSchema) {
    const ERROR_MESSAGE = 'components.forms.errors.money';
    const { state, model } = field;

    if (model === 0) return state.errors.delete(ERROR_MESSAGE);

    const isRequiredWithouModel = (!model || isNaN(model)) && field.settings?.required;
    if (isRequiredWithouModel) return state.errors.add(ERROR_MESSAGE);

    state.errors.delete(ERROR_MESSAGE);
  }

  // CUSTOM VALIDATIONS
  function minLengthValidation(field: FieldSchema, rule: number) {
    const ERROR_MESSAGE = t('components.forms.errors.minLength', { length: rule });
    const { state, model } = field;

    if (!model) return state.errors.delete(ERROR_MESSAGE);

    const isValid = model.length >= rule;
    if (isValid) return state.errors.delete(ERROR_MESSAGE);

    state.errors.add(ERROR_MESSAGE);
  }

  function maxLengthValidation(field: FieldSchema, rule: number) {
    const ERROR_MESSAGE = t('components.forms.errors.maxLength', { length: rule });
    const { state, model } = field;

    if (!model) return state.errors.delete(ERROR_MESSAGE);

    const isValid = model.length <= rule;
    if (isValid) return state.errors.delete(ERROR_MESSAGE);

    state.errors.add(ERROR_MESSAGE);
  }

  // VALIDATION TYPES
  function fieldTypeValidation(field: FieldSchema, wasFormSubmitted: boolean = false) {
    const { type } = field.settings;

    const needsValidation = [
      'amount_unit',
      'contact',
      'contact_type',
      'email',
      'email_type',
      'microchip',
      'money',
      'split_payment',
      'stripe',
      'tel',
    ];
    if (!type || !needsValidation.includes(type)) return;

    //prettier-ignore
    switch (type) {
      case 'amount_unit': return amountFieldValidation(field);
      case 'contact': return field.name === 'email' ? emailFieldValidation(field) : phoneFieldValidation(field);
      case 'contact_type': return contactTypeFieldValidation(field)
      case 'email': return emailFieldValidation(field);
      case 'email_type': return emailTypeFieldValidation(field);
      case 'money': return moneyFieldValidation(field);
      case 'microchip': return microchipFieldValidation(field);
      case 'split_payment': return splitPaymentFieldValidation(field, wasFormSubmitted);
      case 'stripe': return stripeFieldValidation(field);
      case 'tel': return phoneFieldValidation(field);
    }
  }

  function fieldCustomValidation(field: FieldSchema) {
    if (!field.settings?.validations) return;

    const { validations } = field.settings;
    const { MAX_LENGTH, MIN_LENGTH } = FORM_VALIDATIONS;

    validations?.forEach(({ validation, rule }) => {
      //prettier-ignore
      switch (validation) {
        case MIN_LENGTH: return minLengthValidation(field, rule);
        case MAX_LENGTH: maxLengthValidation(field, rule);
      }
    });
  }

  function _requiredField(field: any) {
    const REQUIRED_ERROR_MESSAGE = 'components.forms.errors.required';
    const { model, settings, state } = field;
    const { required } = settings;

    // prettier-ignore
    const modelNotEmpty: boolean = Array.isArray(model) ? !!model.length
      : model === 0 && settings.allowZeroAsValidModel ? true
      : !!model;

    if (required && !modelNotEmpty) {
      state.errors.add(REQUIRED_ERROR_MESSAGE);

      // NOTE: This is for debugging purposes for Gerald. Don't remove this.
      console.log('Errors', field.name, state.errors);
      return;
    }

    state.errors.delete(REQUIRED_ERROR_MESSAGE);
  }

  // HANDLER
  function validateField(field: FieldSchema, wasFormSubmitted: boolean = false) {
    if (field.settings.required && wasFormSubmitted) _requiredField(field);

    fieldTypeValidation(field, wasFormSubmitted);
    fieldCustomValidation(field);
  }

  return {
    emailFieldValidation,
    phoneFieldValidation,
    validateField,
  };
}
